import { useWiring } from '#~/room/components/image/settings/wiring.js';
import type { HtmlAttributes } from '@studio/design-system/props';
import * as Tabs from '@studio/design-system/tabs';
import { useLatestFn } from '@studio/react-utils/hooks';
import type { ReactElement } from 'react';
import { useMemo } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { useStore } from '../store/hooks.js';
import { SUBCATEGORY_TRIGGER_CLASSNAME } from '../types.js';
import { selectVisibleSectionLabels } from './section-visibility.js';

/**
 * Props for the Sections component
 */
export interface SectionsProps extends HtmlAttributes {
  onSectionChange?: (section: string) => void;
  sections: Array<string>;
  currentSection: string;
}

/**
 * Component for displaying video sections tabs
 */
export const Sections = ({
  className,
  onSectionChange,
  sections,
  currentSection,
  ...props
}: SectionsProps): ReactElement => {
  const [candidate] = useStore((µ) => [µ.candidate], shallow);
  const { interfaceSections, conditionsMap } = useWiring(candidate.id);

  const handleChange = useLatestFn((nextValue) => {
    onSectionChange?.(nextValue);
  });

  // Shared with GenericSettingsSection (see section-visibility.ts): the panel
  // decides whether to mount this strip at all from the same predicate, so the
  // two can never disagree about whether the group is empty.
  const filteredSections = useMemo(
    () => selectVisibleSectionLabels(sections, interfaceSections, conditionsMap),
    [sections, interfaceSections, conditionsMap]
  );

  // If there are no sections to display, return an empty fragment
  if (filteredSections.length === 0) {
    return (
      <>
        <div className={twMerge('flex flex-col', className)} {...props}>
          <h3 className='font-semibold text-lg text-foreground'>No settings available</h3>
        </div>
      </>
    );
  }

  return (
    <div className={twMerge('flex flex-col', className)} {...props}>
      <Tabs.Root className='w-full' onChange={handleChange} value={currentSection}>
        <Tabs.List className='mb-28 gap-32 overflow-visible whitespace-nowrap border-0 bg-transparent px-0 text-md'>
          {filteredSections.map((label) => (
            <Tabs.Trigger
              className={twMerge(SUBCATEGORY_TRIGGER_CLASSNAME, 'flex-shrink-0 px-0')}
              key={label}
              value={label}
            >
              <span className={`${currentSection === label ? 'font-demibold' : 'font-medium'}`}>
                {label}
              </span>
            </Tabs.Trigger>
          ))}
        </Tabs.List>
      </Tabs.Root>
    </div>
  );
};
